/**
 * advancedControls.ts — shared definitions for every fine-tuning slider
 * and toggle the renderer exposes.
 *
 * The desktop look panel grew these inline; this file extracts the data
 * so the mobile UI can render the SAME parameters without duplicating
 * 200 lines of pushSlider/pushToggle calls. Both surfaces stay in sync
 * automatically when we add or change a control here.
 */

import { BioclastParams, DEFAULT_BIOCLAST } from "../viz/scenes/bioclast";
import type { SceneControlProfile } from "../viz/scenes/scenePresets";

/**
 * Disclosure tier — drives the mobile "Show advanced" toggle and any
 * future "Lab" surfaces. Anything tagged `advanced` or `lab` is hidden
 * from the default surface to avoid the 27-slider wall of intimidation.
 *
 * Default is `core` when omitted (both per-control and per-section).
 */
export type ControlTier = "core" | "advanced" | "lab";

export interface SliderDef {
  kind: "slider";
  label: string;
  sub: string;
  key: keyof BioclastParams;
  min: number;
  max: number;
  step: number;
  leftHint: string;
  rightHint: string;
  tooltip: string;
  /** Per-control tier override. Falls back to the section's tier. */
  tier?: ControlTier;
}

export interface ToggleDef {
  kind: "toggle";
  label: string;
  sub: string;
  key: keyof BioclastParams;
  tooltip: string;
  tier?: ControlTier;
}

/** Multi-option picker (segmented control on mobile, dropdown on desktop). */
export interface SelectDef {
  kind: "select";
  label: string;
  sub: string;
  key: keyof BioclastParams;
  options: { value: string; label: string }[];
  tooltip: string;
  tier?: ControlTier;
}

export type ControlDef = SliderDef | ToggleDef | SelectDef;

export interface ControlSection {
  title: string;
  /** Optional one-liner shown under the section title in collapsible UI. */
  hint?: string;
  /** Section-level disclosure tier. Default `core`. */
  tier?: ControlTier;
  controls: ControlDef[];
}

export interface SceneControlContext {
  sceneType?: string;
  profile?: SceneControlProfile;
  values?: Partial<BioclastParams>;
}

/**
 * The canonical list of every parameter the user can tune outside of
 * scene picks + per-layer toggles. Order matters — top sections are
 * the most commonly-adjusted.
 */
export const CONTROL_SECTIONS: ControlSection[] = [
  {
    title: "Performance",
    hint: "Higher tiers = denser particle clouds, more GPU/CPU work.",
    controls: [
      {
        kind: "select",
        label: "Quality",
        sub: "particle density tier",
        key: "qualityTier",
        options: [
          { value: "mobile",   label: "Mobile (15K)" },
          { value: "standard", label: "Standard (60K)" },
          { value: "high",     label: "High (120K)" },
          { value: "ultra",    label: "Ultra (240K)" },
          { value: "lab",      label: "Lab (1M)" },
        ],
        tooltip:
          "Particle count for the main field. Mobile is iPhone-friendly; Ultra needs a desktop GPU; Lab targets ~1M particles on high-end hardware.",
      },
      {
        kind: "select",
        label: "Waveform style",
        sub: "shape of the audio overlay",
        key: "waveformStyle",
        options: [
          { value: "line",   label: "Line" },
          { value: "dual",   label: "Dual" },
          { value: "radial", label: "Radial" },
        ],
        tooltip:
          "Style of the waveform overlay. Enable the Waveform layer in Layers → Surfaces.",
      },
    ],
  },
  {
    title: "Look",
    controls: [
      {
        kind: "slider",
        label: "Color Palette",
        sub: "color wheel position",
        key: "paletteHue",
        min: 0,
        max: 360,
        step: 1,
        leftHint: "0",
        rightHint: "360",
        tooltip: "Where the palette starts on the color wheel.",
      },
      {
        kind: "slider",
        label: "Intensity",
        sub: "global brightness",
        key: "intensity",
        min: 0,
        max: 4,
        step: 0.01,
        leftHint: "ghost",
        rightHint: "blazing",
        tooltip:
          "Overdrives particle color brightness. >1 pushes additive blending past full white.",
      },
      {
        kind: "slider",
        label: "Star size",
        sub: "particle dot",
        key: "starSize",
        min: 0.05,
        max: 1,
        step: 0.01,
        leftHint: "tiny",
        rightHint: "huge",
        tooltip: "Per-particle dot size — bigger reads louder on a beat.",
      },
      {
        kind: "slider",
        label: "Saturation",
        sub: "color vividness",
        key: "saturation",
        min: 0,
        max: 1,
        step: 0.01,
        leftHint: "grey",
        rightHint: "neon",
        tooltip: "0 strips color to greyscale; 1 is fully saturated.",
      },
      {
        kind: "slider",
        label: "Opacity",
        sub: "particle alpha",
        key: "opacity",
        min: 0,
        max: 1,
        step: 0.01,
        leftHint: "ghost",
        rightHint: "solid",
        tooltip: "Global transparency of all particles and structures.",
      },
    ],
  },
  {
    title: "Audio Reactivity",
    controls: [
      {
        kind: "slider",
        label: "Bass gravity",
        sub: "kick pull",
        key: "bassGravity",
        min: 0,
        max: 4,
        step: 0.05,
        leftHint: "off",
        rightHint: "huge",
        tooltip: "How much kick/bass pulls particles together and out.",
      },
      {
        kind: "slider",
        label: "Mid curl",
        sub: "swirl",
        key: "midCurl",
        min: 0,
        max: 4,
        step: 0.05,
        leftHint: "off",
        rightHint: "storm",
        tooltip: "How much midrange bends the flow into curling rivers.",
      },
      {
        kind: "slider",
        label: "Treble shimmer",
        sub: "lift / sparkle",
        key: "trebleShimmer",
        min: 0,
        max: 4,
        step: 0.05,
        leftHint: "off",
        rightHint: "sparkle",
        tooltip: "How much treble lifts and twinkles the particles.",
      },
      {
        kind: "slider",
        label: "Cymatic strength",
        sub: "beat shockwaves",
        key: "cymaticStrength",
        min: 0,
        max: 4,
        step: 0.05,
        leftHint: "soft",
        rightHint: "violent",
        tooltip: "Strength of bass-beat shockwave rings expanding outward.",
      },
      {
        kind: "slider",
        label: "Flow speed",
        sub: "field current",
        key: "flowSpeed",
        min: 0,
        max: 4,
        step: 0.01,
        leftHint: "still",
        rightHint: "fast",
        tooltip: "Base current that orbits particles around the galaxy.",
      },
    ],
  },
  {
    title: "Camera",
    hint: "Drag the canvas to orbit + tilt, pinch to zoom.",
    controls: [
      {
        kind: "slider",
        label: "Zoom",
        sub: "distance from scene",
        key: "cameraZoom",
        min: 0.15,
        max: 3.5,
        step: 0.01,
        leftHint: "way out",
        rightHint: "close",
        tooltip:
          "Distance from the scene. Lower values pull way back for a lava-lamp view.",
      },
      {
        kind: "slider",
        label: "Tilt",
        sub: "vertical angle",
        key: "cameraTilt",
        min: 0,
        max: 1,
        step: 0.01,
        leftHint: "horizon",
        rightHint: "top-down",
        tooltip:
          "Vertical view angle. 0 sees the field edge-on, 1 looks straight down.",
      },
      {
        kind: "slider",
        label: "Auto spin",
        sub: "orbit speed",
        key: "cameraSpin",
        min: 0,
        max: 3,
        step: 0.01,
        leftHint: "still",
        rightHint: "fast",
        tooltip:
          "Auto-orbit rate. 0 holds the camera perfectly still after you place it.",
      },
      {
        kind: "slider",
        label: "Flythrough speed",
        sub: "spaceship pace",
        key: "flythroughSpeed",
        min: 0.0,
        max: 3,
        step: 0.01,
        leftHint: "stop",
        rightHint: "warp",
        tooltip: "Speed of the flythrough path (only used in Flythrough mode).",
      },
      {
        kind: "slider",
        label: "Beat dolly",
        sub: "punch on beat",
        key: "beatDolly",
        min: 0,
        max: 1,
        step: 0.01,
        leftHint: "off",
        rightHint: "punchy",
        tooltip: "Forward camera lurch on each detected beat (flythrough mode).",
      },
    ],
  },
  {
    title: "Field Shape",
    tier: "advanced",
    controls: [
      {
        kind: "slider",
        label: "Field tightness",
        sub: "squeeze / spread",
        key: "fieldTightness",
        min: 0.4,
        max: 1.6,
        step: 0.01,
        leftHint: "tight",
        rightHint: "wide",
        tooltip: "Squeezes (low) or fans out (high) the entire particle field.",
      },
    ],
  },
  {
    title: "Bloom Glow",
    hint: "Neon halo post-processing.",
    tier: "advanced",
    controls: [
      {
        kind: "toggle",
        label: "Bloom enabled",
        sub: "neon glow post-fx",
        key: "bloomEnabled",
        tooltip: "Adds a soft neon halo around bright pixels.",
      },
      {
        kind: "slider",
        label: "Bloom strength",
        sub: "halo intensity",
        key: "bloomStrength",
        min: 0,
        max: 3,
        step: 0.01,
        leftHint: "subtle",
        rightHint: "blazing",
        tooltip: "How brightly bloomed pixels glow on top of the scene.",
      },
      {
        kind: "slider",
        label: "Bloom radius",
        sub: "halo spread",
        key: "bloomRadius",
        min: 0,
        max: 1.2,
        step: 0.01,
        leftHint: "tight",
        rightHint: "wide",
        tooltip: "How far the glow blurs out from each bright pixel.",
      },
      {
        kind: "slider",
        label: "Bloom threshold",
        sub: "what blooms",
        key: "bloomThreshold",
        min: 0,
        max: 1,
        step: 0.01,
        leftHint: "everything",
        rightHint: "only bright",
        tooltip:
          "Only pixels brighter than this value bloom. 0 = whole scene glows; 1 = almost nothing does.",
      },
    ],
  },
  {
    title: "Trails",
    tier: "lab",
    hint: "Frame-feedback effect — particles leave swirling trails like MilkDrop. Set Strength > 0 to enable.",
    controls: [
      {
        kind: "slider",
        label: "Trail strength",
        sub: "how much of last frame to keep",
        key: "feedbackStrength",
        min: 0,
        max: 0.95,
        step: 0.01,
        leftHint: "off",
        rightHint: "heavy",
        tooltip:
          "0 disables trails entirely (zero perf cost). Above 0, the previous frame is blended in each frame for an organic 'history' look.",
      },
      {
        kind: "slider",
        label: "Trail decay",
        sub: "how fast trails fade",
        key: "feedbackDecay",
        min: 0.8,
        max: 1.0,
        step: 0.005,
        leftHint: "fast fade",
        rightHint: "slow fade",
        tooltip:
          "Closer to 1 = trails persist longer. 0.96 is a balanced default; 0.99 looks like infinite history.",
      },
      {
        kind: "slider",
        label: "Trail zoom",
        sub: "radial pull-in/out",
        key: "feedbackZoom",
        min: -0.05,
        max: 0.05,
        step: 0.001,
        leftHint: "outward",
        rightHint: "inward",
        tooltip:
          "Per-frame zoom of the previous frame. Negative pushes the trail outward (explosion); positive pulls it inward (vortex).",
      },
      {
        kind: "slider",
        label: "Trail rotate",
        sub: "spin the trails",
        key: "feedbackRotate",
        min: -0.05,
        max: 0.05,
        step: 0.001,
        leftHint: "left",
        rightHint: "right",
        tooltip:
          "Per-frame rotation. Combine with Trail strength > 0.7 for a hypnotic spiral effect.",
      },
      {
        kind: "slider",
        label: "Trail drift X",
        sub: "horizontal trail offset",
        key: "feedbackDriftX",
        min: -0.02,
        max: 0.02,
        step: 0.0005,
        leftHint: "left",
        rightHint: "right",
        tooltip: "Per-frame horizontal drift of the previous frame.",
      },
      {
        kind: "slider",
        label: "Trail drift Y",
        sub: "vertical trail offset",
        key: "feedbackDriftY",
        min: -0.02,
        max: 0.02,
        step: 0.0005,
        leftHint: "down",
        rightHint: "up",
        tooltip: "Per-frame vertical drift of the previous frame.",
      },
    ],
  },
  {
    title: "Energy Orb",
    tier: "advanced",
    controls: [
      {
        kind: "slider",
        label: "Orb size",
        sub: "world scale",
        key: "energyOrbSize",
        min: 0.1,
        max: 0.8,
        step: 0.01,
        leftHint: "small",
        rightHint: "huge",
        tooltip: "Scale of the volumetric Energy Orb layer.",
      },
      {
        kind: "slider",
        label: "Orb density",
        sub: "internal emission",
        key: "energyOrbDensity",
        min: 0.1,
        max: 3,
        step: 0.01,
        leftHint: "mist",
        rightHint: "fire",
        tooltip: "How much light accumulates inside the raymarched orb.",
      },
      {
        kind: "slider",
        label: "Orb speed",
        sub: "internal motion",
        key: "energyOrbSpeed",
        min: 0.05,
        max: 3,
        step: 0.01,
        leftHint: "slow",
        rightHint: "fast",
        tooltip: "Animation speed of the folded field inside the orb.",
      },
      {
        kind: "slider",
        label: "Halo glow",
        sub: "outer shell",
        key: "energyOrbAtmosphereGlow",
        min: 0,
        max: 5,
        step: 0.01,
        leftHint: "off",
        rightHint: "flare",
        tooltip: "Strength of the additive atmosphere shell around the orb.",
      },
      {
        kind: "slider",
        label: "Fold scale",
        sub: "fractal structure",
        key: "energyOrbFractalScale",
        min: 0.3,
        max: 1.5,
        step: 0.01,
        leftHint: "soft",
        rightHint: "sharp",
        tooltip: "Scale factor for the repeating folds inside the volume.",
      },
      {
        kind: "slider",
        label: "Fold count",
        sub: "raymarch complexity",
        key: "energyOrbFractalIters",
        min: 2,
        max: 8,
        step: 1,
        leftHint: "open",
        rightHint: "dense",
        tooltip: "Fractal iterations used by the Energy Orb shader. Higher values cost more GPU time.",
        tier: "lab",
      },
      {
        kind: "slider",
        label: "Fold smoothness",
        sub: "internal softness",
        key: "energyOrbSmoothness",
        min: 0,
        max: 0.15,
        step: 0.001,
        leftHint: "crisp",
        rightHint: "silky",
        tooltip: "Softens the fold math inside the raymarched volume.",
        tier: "lab",
      },
      {
        kind: "slider",
        label: "Asymmetry",
        sub: "organic offset",
        key: "energyOrbAsymmetry",
        min: 0,
        max: 1,
        step: 0.01,
        leftHint: "mirror",
        rightHint: "wild",
        tooltip: "Breaks the perfect mirror symmetry of the folded orb field.",
        tier: "lab",
      },
    ],
  },
  {
    title: "Atmosphere",
    tier: "advanced",
    controls: [
      {
        kind: "slider",
        label: "Glow",
        sub: "backdrop wash",
        key: "atmosphere",
        min: 0,
        max: 1.5,
        step: 0.01,
        leftHint: "off",
        rightHint: "bright",
        tooltip: "Strength of the atmospheric color wash behind the visualizer.",
      },
      {
        kind: "slider",
        label: "Blur",
        sub: "backdrop softness",
        key: "backdropBlur",
        min: 0,
        max: 120,
        step: 1,
        leftHint: "crisp",
        rightHint: "soft",
        tooltip: "Blur radius of the backdrop wash. Does not blur particles.",
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Registry helpers — used by mobileAdvancedPanel for progressive disclosure
// and per-section reset.
// ---------------------------------------------------------------------------

/** Tier ordering used for "show controls at or below `tier`" filtering. */
const TIER_RANK: Record<ControlTier, number> = {
  core: 0,
  advanced: 1,
  lab: 2,
};

/**
 * Resolve a control's effective tier. Falls back to its parent section's
 * tier, then to "core".
 */
export function effectiveTier(
  ctrl: ControlDef,
  section: ControlSection,
): ControlTier {
  return ctrl.tier ?? section.tier ?? "core";
}

/**
 * Filter sections + controls down to those at or below the requested
 * disclosure tier. Sections that end up empty are dropped so the UI
 * doesn't render headerless husks.
 *
 *   filterByTier("core")     → only the must-haves (Performance, Look, …)
 *   filterByTier("advanced") → adds Bloom, Field Shape, Atmosphere
 *   filterByTier("lab")      → adds the experimental Trails feedback rig
 */
export function filterByTier(tier: ControlTier): ControlSection[] {
  const max = TIER_RANK[tier];
  const out: ControlSection[] = [];
  for (const section of CONTROL_SECTIONS) {
    const sectionTier = section.tier ?? "core";
    // If the whole section is above the cap, every control is too.
    if (TIER_RANK[sectionTier] > max) continue;
    const controls = section.controls.filter(
      (c) => TIER_RANK[effectiveTier(c, section)] <= max,
    );
    if (controls.length === 0) continue;
    out.push({ ...section, controls });
  }
  return out;
}

const commonSceneKeys: Array<keyof BioclastParams> = [
  "paletteHue",
  "intensity",
  "saturation",
  "opacity",
  "bassGravity",
  "midCurl",
  "trebleShimmer",
  "cymaticStrength",
  "flowSpeed",
  "cameraZoom",
  "cameraTilt",
  "cameraSpin",
  "flythroughSpeed",
  "beatDolly",
  "bloomEnabled",
  "bloomStrength",
  "bloomRadius",
  "bloomThreshold",
  "atmosphere",
];

const energyOrbKeys: Array<keyof BioclastParams> = [
  "energyOrbSize",
  "energyOrbDensity",
  "energyOrbSpeed",
  "energyOrbAtmosphereGlow",
  "energyOrbFractalScale",
  "energyOrbFractalIters",
  "energyOrbSmoothness",
  "energyOrbAsymmetry",
];

const texturedSphereKeys: Array<keyof BioclastParams> = [
  "sphereDetail",
  "sphereSize",
  "sphereDisplacement",
  "sphereVertexAudioMix",
  "sphereNoiseScale",
  "sphereNoiseIntensity",
  "sphereNoiseSpeed",
  "sphereWaveFrequency",
  "sphereWaveSpeed",
  "sphereColorIntensity",
  "sphereMotionSmoothing",
  "sphereRotationSpeed",
  "sphereSolidInterior",
];

const meshSceneKeys: Array<keyof BioclastParams> = [
  "meshGridWidth",
  "meshGridDepth",
  "meshCellSize",
  "meshWaveHeight",
  "meshWaveDepth",
  "meshComplexity",
  "meshHarmonics",
  "meshBeatSharpness",
];

const mathParticleKeys: Array<keyof BioclastParams> = [
  "mathParticleCount",
  "mathPointSize",
  "mathAmplitude",
  "mathFrequency",
  "mathRotationSpeed",
  "mathDepthScale",
  "mathWarmth",
];

function keySet(
  keys: Array<keyof BioclastParams>,
): Set<keyof BioclastParams> {
  return new Set(keys);
}

const SCENE_PROFILE_KEYS: Record<SceneControlProfile, Set<keyof BioclastParams>> = {
  bioclast: keySet([
    ...commonSceneKeys,
    "fieldTightness",
    "showShockwave",
    "showStarbursts",
  ]),
  energyOrb: keySet([...commonSceneKeys, ...energyOrbKeys]),
  texturedSphere: keySet([...commonSceneKeys, ...texturedSphereKeys]),
  mathParticles: keySet([...commonSceneKeys, ...mathParticleKeys]),
  mesh: keySet([...commonSceneKeys, ...meshSceneKeys]),
};

const MESH_VISUALIZER_SECTION: ControlSection = {
  title: "Mesh Visualizer",
  hint: "Procedural mesh terrain shared by Audio Tide, Mercury Pool, Mountain Range.",
  tier: "advanced",
  controls: [
    {
      kind: "slider",
      label: "Grid width",
      sub: "cells across",
      key: "meshGridWidth",
      min: 20,
      max: 200,
      step: 1,
      leftHint: "sparse",
      rightHint: "dense",
      tooltip: "Number of mesh cells across the horizontal axis.",
    },
    {
      kind: "slider",
      label: "Grid depth",
      sub: "cells deep",
      key: "meshGridDepth",
      min: 20,
      max: 200,
      step: 1,
      leftHint: "shallow",
      rightHint: "deep",
      tooltip: "Number of mesh cells along the depth axis.",
    },
    {
      kind: "slider",
      label: "Cell size",
      sub: "world units",
      key: "meshCellSize",
      min: 0.05,
      max: 0.5,
      step: 0.01,
      leftHint: "tight",
      rightHint: "loose",
      tooltip: "World-space size of each mesh cell. Larger = bigger features.",
    },
    {
      kind: "slider",
      label: "Wave height",
      sub: "vertical amp",
      key: "meshWaveHeight",
      min: 0,
      max: 3,
      step: 0.05,
      leftHint: "flat",
      rightHint: "peaks",
      tooltip: "How tall the mesh waves rise on audio peaks.",
    },
    {
      kind: "slider",
      label: "Wave depth",
      sub: "depth amp",
      key: "meshWaveDepth",
      min: 0,
      max: 2,
      step: 0.05,
      leftHint: "thin",
      rightHint: "thick",
      tooltip: "How far waves penetrate into the depth axis.",
    },
    {
      kind: "slider",
      label: "Complexity",
      sub: "noise octaves",
      key: "meshComplexity",
      min: 0,
      max: 1,
      step: 0.01,
      leftHint: "smooth",
      rightHint: "jagged",
      tooltip: "Procedural detail — higher values stack more noise octaves.",
    },
    {
      kind: "slider",
      label: "Harmonics",
      sub: "overtones",
      key: "meshHarmonics",
      min: 0,
      max: 1,
      step: 0.01,
      leftHint: "pure",
      rightHint: "rich",
      tooltip: "Strength of harmonic overtones layered on the base wave.",
    },
    {
      kind: "slider",
      label: "Beat sharpness",
      sub: "impact response",
      key: "meshBeatSharpness",
      min: 0,
      max: 1,
      step: 0.01,
      leftHint: "soft",
      rightHint: "hard",
      tooltip: "How sharply the mesh snaps on each detected beat.",
    },
  ],
};

const MATH_PARTICLE_SECTION: ControlSection = {
  title: "Math Particles",
  hint: "Density and topology controls for the live shader math scenes.",
  tier: "advanced",
  controls: [
    {
      kind: "slider",
      label: "Math particles",
      sub: "topology density",
      key: "mathParticleCount",
      min: 4_000,
      max: 120_000,
      step: 1_000,
      leftHint: "sparse",
      rightHint: "dense",
      tooltip: "Active particle count for the live math shader, capped by the selected quality tier.",
    },
    {
      kind: "slider",
      label: "Math point size",
      sub: "shader dots",
      key: "mathPointSize",
      min: 0.25,
      max: 2.4,
      step: 0.01,
      leftHint: "pinpoint",
      rightHint: "halo",
      tooltip: "Size of each rendered math particle before audio and distance scaling.",
    },
    {
      kind: "slider",
      label: "Math amplitude",
      sub: "surface deformation",
      key: "mathAmplitude",
      min: 0,
      max: 1.6,
      step: 0.01,
      leftHint: "calm",
      rightHint: "warped",
      tooltip: "Strength of the live math surface deformation and beat expansion.",
    },
    {
      kind: "slider",
      label: "Math frequency",
      sub: "topology detail",
      key: "mathFrequency",
      min: 0.4,
      max: 6,
      step: 0.01,
      leftHint: "broad",
      rightHint: "fine",
      tooltip: "Frequency/detail control used by rings, petals, fibers, and spiral ribs.",
    },
    {
      kind: "slider",
      label: "Math orbit",
      sub: "phase speed",
      key: "mathRotationSpeed",
      min: 0,
      max: 2.5,
      step: 0.01,
      leftHint: "still",
      rightHint: "fast",
      tooltip: "Orbit and phase speed for the live shader math topology.",
    },
    {
      kind: "slider",
      label: "Math depth",
      sub: "projection scale",
      key: "mathDepthScale",
      min: 0,
      max: 1.4,
      step: 0.01,
      leftHint: "flat",
      rightHint: "deep",
      tooltip: "Depth and projection spread used by Hopf, Fibonacci, and orbit motion.",
    },
    {
      kind: "slider",
      label: "Gold / blue mix",
      sub: "reference warmth",
      key: "mathWarmth",
      min: 0,
      max: 1,
      step: 0.01,
      leftHint: "blue",
      rightHint: "gold",
      tooltip: "Mixes the restored reference palette between cooler teal-blue accents and warm gold.",
    },
  ],
};

const TEXTURED_SPHERE_SECTION: ControlSection = {
  title: "Sphere Surface",
  tier: "advanced",
  controls: [
    {
      kind: "slider",
      label: "Sphere detail",
      sub: "mesh resolution",
      key: "sphereDetail",
      min: 32,
      max: 192,
      step: 1,
      leftHint: "light",
      rightHint: "dense",
      tooltip: "Resolution of the audio-reactive sphere mesh.",
    },
    {
      kind: "slider",
      label: "Sphere size",
      sub: "world scale",
      key: "sphereSize",
      min: 0.6,
      max: 3,
      step: 0.01,
      leftHint: "small",
      rightHint: "huge",
      tooltip: "Scale of the textured sphere in the scene.",
    },
    {
      kind: "slider",
      label: "Displacement",
      sub: "surface depth",
      key: "sphereDisplacement",
      min: 0,
      max: 1.2,
      step: 0.01,
      leftHint: "smooth",
      rightHint: "folded",
      tooltip: "How far audio and noise push the sphere surface.",
    },
    {
      kind: "slider",
      label: "Audio mix",
      sub: "beat deformation",
      key: "sphereVertexAudioMix",
      min: 0,
      max: 1.5,
      step: 0.01,
      leftHint: "calm",
      rightHint: "reactive",
      tooltip: "How strongly bass, mids, and treble shape the sphere vertices.",
    },
    {
      kind: "slider",
      label: "Noise scale",
      sub: "fold spacing",
      key: "sphereNoiseScale",
      min: 0.4,
      max: 6,
      step: 0.01,
      leftHint: "broad",
      rightHint: "fine",
      tooltip: "Spatial scale of the folded surface noise.",
    },
    {
      kind: "slider",
      label: "Noise intensity",
      sub: "surface contrast",
      key: "sphereNoiseIntensity",
      min: 0,
      max: 1.5,
      step: 0.01,
      leftHint: "soft",
      rightHint: "sharp",
      tooltip: "Contrast of the animated surface bands.",
    },
    {
      kind: "slider",
      label: "Noise speed",
      sub: "texture flow",
      key: "sphereNoiseSpeed",
      min: 0,
      max: 2,
      step: 0.01,
      leftHint: "still",
      rightHint: "fast",
      tooltip: "Animation speed of the moving surface texture.",
    },
    {
      kind: "slider",
      label: "Wave speed",
      sub: "audio ripple pace",
      key: "sphereWaveSpeed",
      min: 0,
      max: 2,
      step: 0.01,
      leftHint: "slow",
      rightHint: "fast",
      tooltip: "Speed of the sphere's audio-reactive ripples.",
    },
    {
      kind: "slider",
      label: "Color intensity",
      sub: "surface luminance",
      key: "sphereColorIntensity",
      min: 0,
      max: 4,
      step: 0.01,
      leftHint: "dim",
      rightHint: "vivid",
      tooltip: "Brightness multiplier for the sphere surface colors.",
    },
    {
      kind: "slider",
      label: "Rotation speed",
      sub: "sphere spin",
      key: "sphereRotationSpeed",
      min: 0,
      max: 2,
      step: 0.01,
      leftHint: "still",
      rightHint: "fast",
      tooltip: "Continuous rotation speed for the sphere.",
    },
    {
      kind: "toggle",
      label: "Solid interior",
      sub: "inner core",
      key: "sphereSolidInterior",
      tooltip: "Shows or hides the dark interior sphere core.",
    },
  ],
};

function inferSceneControlProfile(
  context: SceneControlContext,
): SceneControlProfile {
  if (context.profile) return context.profile;
  if (context.sceneType === "texturedSphere") return "texturedSphere";
  if (
    context.sceneType === "superform" ||
    context.sceneType === "spiralVortex" ||
    context.sceneType === "hopfFibration" ||
    context.sceneType === "fibonacciSequence" ||
    context.sceneType === "radialPulse" ||
    context.sceneType === "mathSuperform" ||
    context.sceneType === "mathFibonacci" ||
    context.sceneType === "mathRadialPulse" ||
    context.sceneType === "sineSurge" ||
    context.sceneType === "stellarator" ||
    context.sceneType === "plasmaSheet" ||
    context.sceneType === "noiseBloom" ||
    context.sceneType === "harmonicShell" ||
    context.sceneType === "strangeAttractor" ||
    context.sceneType === "lorenzDrift" ||
    context.sceneType === "organicBreath" ||
    context.sceneType === "helicalTwist"
  ) {
    return "mathParticles";
  }
  if (context.values?.layerEnergyOrb) return "energyOrb";
  return "bioclast";
}

/**
 * Filter the shared control registry to knobs that are meaningful for the
 * selected scene recipe. This keeps the Scene tab truthful while preserving
 * the full registry for deeper mobile/lab surfaces.
 */
export function filterBySceneProfile(
  context: SceneControlContext,
  tier: ControlTier = "advanced",
): ControlSection[] {
  const profile = inferSceneControlProfile(context);
  const allowed = SCENE_PROFILE_KEYS[profile];
  const max = TIER_RANK[tier];
  const sourceSections = filterByTier(tier);
  if (profile === "texturedSphere") {
    sourceSections.push(TEXTURED_SPHERE_SECTION);
  }
  if (profile === "mathParticles") {
    sourceSections.push(MATH_PARTICLE_SECTION);
  }
  if (profile === "mesh") {
    sourceSections.push(MESH_VISUALIZER_SECTION);
  }

  const out: ControlSection[] = [];
  for (const source of sourceSections) {
    const sectionTier = source.tier ?? "core";
    if (TIER_RANK[sectionTier] > max) continue;
    const controls = source.controls.filter(
      (ctrl) =>
        allowed.has(ctrl.key) &&
        TIER_RANK[effectiveTier(ctrl, source)] <= max,
    );
    if (!controls.length) continue;
    out.push({ ...source, controls });
  }
  return out;
}

/**
 * Snapshot of every control's default value in a section, keyed by
 * `BioclastParams` field. Used by per-section "Reset" actions to revert
 * just the section the user is editing without touching the rest.
 */
export function defaultsFor(section: ControlSection): Partial<BioclastParams> {
  const out: Record<string, unknown> = {};
  const defaults = DEFAULT_BIOCLAST as unknown as Record<string, unknown>;
  for (const ctrl of section.controls) {
    const k = ctrl.key as string;
    if (k in defaults) out[k] = defaults[k];
  }
  return out as Partial<BioclastParams>;
}
